import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from '@aml/amount';
import { Text, StyleSheet, View } from '@react-pdf/renderer';
import { BalanceToken, Cluster, Maybe } from '@apolloGenerated';
import { BalancePDF } from '@component/downloadPDF/ui/BalancePDF';
import { setFormatDate } from '@utils/timestamp';

interface Props {
  address: string;
  network: string;
  balance?: {
    balance: string | number;
    balanceIn: string | number;
    balanceOut: string | number;
  };
  tokens?: BalanceToken[];
  risk?: number;
  categoryName?: string;
  transaction?: {
    total: string | number;
    totalSent: string | number;
    totalReceived: string | number;
  };
  activity?: {
    first: string;
    last: string;
  };
  cluster?: Maybe<Cluster>;
  owner?: string | null;
}

const styles = StyleSheet.create({
  address: {
    fontSize: 14,
    marginBottom: 12,
  },
  groupHorizontal: {
    flexWrap: 'wrap',
    flexDirection: 'row',
  },
  groupBetween: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  groupVertical: {
    flexDirection: 'column',
    paddingRight: 6,
    paddingLeft: 6,
    borderRightWidth: 1,
    borderColor: '#dfe2eb',
  },
  title: {
    fontSize: 8,
    color: '#69789F',
    marginBottom: 4,
    marginRight: 4,
  },
  subtitle: {
    fontSize: 8,
    color: '#69789F',
    marginRight: 8,
  },
  text: {
    fontSize: 8,
    color: '#3c4654',
  },
  textSmall: {
    fontSize: 8,
  },
  textRed: {
    fontSize: 8,
    color: '#B8433D',
  },
  textGreen: {
    fontSize: 8,
    color: '#3D7A21',
  },
  riskDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
  },
});

const getRiskColor = (riskValue: number): string => {
  if (riskValue <= 25) return '#3D7A21';
  if (riskValue >= 75) return '#B8433D';
  return '#ffb400';
};

export const Header: FC<Props> = ({
  network,
  address,
  risk,
  balance,
  tokens,
  categoryName,
  transaction,
  activity,
  cluster,
  owner,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <Text style={styles.address}>{address}</Text>
      <View style={styles.groupHorizontal}>
        <View style={styles.groupVertical}>
          {/* RISK */}
          {!!risk && (
            <View style={styles.groupHorizontal}>
              <Text style={styles.title}>{t('strings.riskscore')}</Text>
              <View style={[styles.groupHorizontal, { alignItems: 'center' }]}>
                <Text
                  style={[
                    styles.riskDot,
                    { backgroundColor: getRiskColor(risk), marginRight: '4px' },
                  ]}
                ></Text>
                <Text style={styles.textSmall}>{risk}%</Text>
              </View>
            </View>
          )}
          {/* CLUSTER */}
          {!!cluster && (
            <View style={styles.groupHorizontal}>
              <Text style={styles.title}>{t('mention.cluster')}</Text>
              <Text style={[styles.textRed, { color: '#034AB3' }]}>
                {String(cluster?.owner) || 'Entity'}
              </Text>
            </View>
          )}
          {/* CATEGORY */}
          {!!categoryName && (
            <View style={styles.groupHorizontal}>
              <Text style={styles.title}>{t('strings.reported')}</Text>
              <Text style={styles.textRed}>{categoryName}</Text>
            </View>
          )}
          {!!owner && (
            <View style={styles.groupHorizontal}>
              <Text style={styles.title}>{t('strings.owner')}</Text>
              <Text style={styles.textRed}>{owner}</Text>
            </View>
          )}
        </View>

        {/* BALANCE */}
        {balance && <BalancePDF network={network} balance={balance} tokens={tokens} />}

        {/* TRANSACTION */}
        {!!transaction?.total && (
          <View style={styles.groupVertical}>
            <View style={styles.groupBetween}>
              <Text style={styles.title}>{t('strings.transactions')}</Text>
              <Text style={styles.textSmall}>{format(transaction.total, 0, 0)}</Text>
            </View>
            <View style={styles.groupBetween}>
              <Text style={styles.title}>{t('strings.received')}</Text>
              <Text style={[styles.textSmall, styles.textGreen]}>
                +{format(transaction.totalReceived, 0, 0)}
              </Text>
            </View>
            <View style={styles.groupBetween}>
              <Text style={styles.title}>{t('strings.sent')}</Text>
              <Text style={[styles.textSmall, styles.textRed]}>
                -{format(transaction.totalSent, 0, 0)}
              </Text>
            </View>
          </View>
        )}

        {/* ACTIVITY */}
        {!!activity && (
          <View style={[styles.groupVertical, { borderWidth: 0 }]}>
            <Text style={styles.title}>{t('titles.activity')}</Text>
            <Text style={styles.textSmall}>
              {/*@ts-ignore*/}
              {setFormatDate(activity.first)}-{setFormatDate(activity.last)}
            </Text>
          </View>
        )}
      </View>
    </>
  );
};
